import TableWrapper from "components/common/table-wrapper";
import { Plus, Pencil, Trash } from "phosphor-react";
import { useCallback, useEffect, useState } from "react";
import Tooltip from "components/common/tooltip";
import { DEBOUNCE_DELAY, initialMetaForTable } from "constants/common";
import CustomModal from "components/common/modal";
import AddSubCategoryForm from "./AddSubCategoryForm";
import {
  createExpenseSubCategory,
  getExpenseSubCategories,
  updateExpenseSubCategory,
  deleteExpenseSubCategory,
} from "containers/configuration/api";
import {
  getUserRole,
  canViewOnlyConfiguration,
  isAdminUser,
  highlightFirstMatch,
} from "utils/common";
import debounce from "lodash/debounce";

const SubCategories = () => {
  const [search, setSearch] = useState("");
  const [selectedConfigration, setSelectedConfirgration] = useState(0);
  const [isSubCategoryModalVisible, setIsSubCategoryModalVisible] = useState(
    false,
  );
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [currentSubCategoryEdit, setCurrentSubCategoryEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({
    ...initialMetaForTable,
    mif_status: "active",
    perPage: "10",
    page: 1,
  });
  const [totalCount, setTotalCount] = useState(0);

  const userRole = getUserRole();
  const isViewOnly = canViewOnlyConfiguration(userRole);
  const hasAdminFlag = isAdminUser();
  const isAdminRole = userRole === "Admin";

  const fetchSubCategories = async (payload) => {
    setLoading(true);
    try {
      const response = await getExpenseSubCategories(payload);
      if (response?.expense_sub_categories) {
        setSubCategoryList(response.expense_sub_categories);
        setTotalCount(response.meta?.total_count || 0);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetSearchQuery = (value) => {
    setMeta((prev) => ({
      ...prev,
      search: value,
      page: 1,
    }));
  };

  const debouncedSearch = useCallback(
    debounce(handleSetSearchQuery, DEBOUNCE_DELAY),
    [],
  );
  useEffect(() => {
    const payload = {
      ...meta,
      order: meta.order,
      sort: meta.sort,
    };

    fetchSubCategories(payload);
  }, [meta]);

  const deleteSelectedSubCategory = async (id) => {
    try {
      await deleteExpenseSubCategory(id);
      fetchSubCategories(meta);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubCategorySubmission = async (data) => {
    try {
      const isEditMode = Boolean(currentSubCategoryEdit);

      let response;

      if (isEditMode) {
        response = await updateExpenseSubCategory(
          data,
          currentSubCategoryEdit.id,
        );
      } else {
        response = await createExpenseSubCategory(data);
      }

      if (response) {
        setIsSubCategoryModalVisible(false);
        setCurrentSubCategoryEdit(null);
        fetchSubCategories(meta);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handlePageChange = (page) => {
    setMeta((prev) => ({ ...prev, page }));
  };
  return (
    <>
      <TableWrapper
        searchPlaceholder="Search Sub Category"
        searchValue={search}
        setSearhQuery={(query) => {
          setSearch(query);
          debouncedSearch(query);
        }}
        totalListCount={totalCount}
        pageSize={meta.perPage}
        currentPage={meta.page}
        onPageChange={handlePageChange}
        selectedItems=""
        isLoading={loading}
        meta={meta}
        setMeta={setMeta}
        handleRefreshPage={fetchSubCategories}
        actionButtons={
          // View-only users (CS Manager, Executive, Accounting without admin flag) see no Add button
          // Admin role sees no Add button (only Edit)
          // Users with admin flag get Edit from admin permissions, but not Add (admin doesn't get Add)
          isViewOnly || !hasAdminFlag
            ? [] // No Add button for view-only users, Admin role, or users with admin flag
            : selectedConfigration > 0
            ? []
            : [
                {
                  label: "Add Sub Category",
                  classes: "primary-btn",
                  icon: <Plus size={24} className="me-3" />,
                  handleClick: () => setIsSubCategoryModalVisible(true),
                },
              ]
        }
      >
        <div className="container-fluid">
          <div className="overflow-scroll border-radius-20px">
            <table className="table table-rows">
              <thead
                className="table-header"
                style={{ backgroundColor: "black", color: "white" }}
              >
                <tr>
                  <th>Sub Category</th>
                  <th>Expense Category</th>
                  <th>Account Number</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {subCategoryList && subCategoryList.length > 0 ? (
                  subCategoryList.map((item, index) => (
                    <tr key={index}>
                      <td className="main-heading">
                        {highlightFirstMatch(item?.name, meta.search)}
                      </td>
                      <td>{item?.expense_category?.name || "-"}</td>
                      <td>{item?.account_number || "-"}</td>
                      <td>
                        <span
                          className={`status ${
                            item?.status === "active" ? "Active" : "Inactive"
                          }`}
                        >
                          {item?.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        {/* View-only users don't see Edit/Delete */}
                        {/* Users with admin flag get Edit from admin permissions */}
                        {/* Admin role only sees Edit (not Delete) */}
                        {(!isViewOnly || hasAdminFlag || isAdminRole) && (
                          <>
                            <Tooltip title="Edit">
                              <Pencil
                                size={24}
                                className="opacity-50 me-4"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  setCurrentSubCategoryEdit(item);
                                  setIsSubCategoryModalVisible(true);
                                }}
                              />
                            </Tooltip>
                            {/* Admin role and users with admin flag don't see Delete */}
                            {!isAdminRole && !hasAdminFlag && (
                              <Tooltip title="Delete">
                                <Trash
                                  size={24}
                                  className="opacity-50 me-4"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    deleteSelectedSubCategory(item.id);
                                  }}
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-3 text-gray-500">
                      No Sub Categories Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </TableWrapper>

      {isSubCategoryModalVisible && (
        <CustomModal
          size="sm"
          show
          onHide={() => {
            setIsSubCategoryModalVisible(false);
            setCurrentSubCategoryEdit(null);
          }}
          heading={
            currentSubCategoryEdit
              ? "Edit Expense Category"
              : "Add Expense Category"
          }
        >
          <AddSubCategoryForm
            handleSubCategorySubmission={handleSubCategorySubmission}
            currentSubCategoryEdit={currentSubCategoryEdit}
            handleClose={() => {
              setIsSubCategoryModalVisible(false);
              setCurrentSubCategoryEdit(null);
            }}
          />
        </CustomModal>
      )}
    </>
  );
};

export default SubCategories;
